import { useClerk } from "@clerk/tanstack-react-start"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useRef, useState } from "react"
import { Spinner } from "#/components/ui/spinner"

export const Route = createFileRoute("/demo/$uuid")({
	validateSearch: (search: Record<string, unknown>) => ({
		ticket: (search.ticket as string) || "",
		orgId: (search.orgId as string) || "",
	}),
	component: DemoActivation,
})

function DemoActivation() {
	const { ticket, orgId } = Route.useSearch()
	const { uuid } = Route.useParams()
	const clerk = useClerk()
	const navigate = useNavigate()
	const [error, setError] = useState<string | null>(null)
	const attempted = useRef(false)

	const hasMissingParams = !ticket || !orgId

	useEffect(() => {
		if (!clerk.loaded || hasMissingParams || attempted.current) return
		attempted.current = true

		const activate = async () => {
			try {
				console.log("[demo:activate] Starting. clerk.loaded:", clerk.loaded)

				// Don't sign out first — setActive replaces the session atomically.
				// Calling signOut() triggers route re-evaluation which breaks beforeLoad.
				console.log("[demo:activate] Creating sign-in with ticket strategy...")
				const result = await clerk.client.signIn.create({ strategy: "ticket", ticket })
				console.log("[demo:activate] Result status:", result.status)

				if (result.status === "complete") {
					await clerk.setActive({
						session: result.createdSessionId,
						organization: orgId,
					})
					console.log("[demo:activate] Session active, navigating to /stonelog")
					navigate({ to: "/stonelog", search: { demo: uuid } })
				} else {
					setError("Sign-in could not be completed. Please try again.")
				}
			} catch (err) {
				console.error("[demo:activate] Error:", err)
				setError(err instanceof Error ? err.message : "Failed to activate demo. Please try again.")
			}
		}

		activate()
	}, [clerk.loaded, clerk, hasMissingParams, ticket, orgId, navigate, uuid])

	if (hasMissingParams) {
		return (
			<DemoLayout>
				<h1 className="font-heading text-xl font-medium text-foreground">Demo link expired</h1>
				<p className="mt-2 text-sm text-foreground-secondary">
					This demo link has expired or is invalid.
				</p>
				<Link to="/demo" className="mt-6 inline-block text-sm text-foreground underline">
					Create a new demo
				</Link>
			</DemoLayout>
		)
	}

	if (error) {
		return (
			<DemoLayout>
				<h1 className="font-heading text-xl font-medium text-foreground">Something went wrong</h1>
				<p className="mt-2 text-sm text-severe">{error}</p>
				<Link to="/demo" className="mt-6 inline-block text-sm text-foreground underline">
					Try again
				</Link>
			</DemoLayout>
		)
	}

	return (
		<main className="flex min-h-screen items-center justify-center bg-background px-4">
			<div className="text-center">
				<Spinner className="mx-auto size-8 text-foreground-secondary" />
				<p className="mt-4 text-sm text-foreground-secondary">
					Setting up your demo environment...
				</p>
			</div>
		</main>
	)
}

function DemoLayout({ children }: { children: React.ReactNode }) {
	return (
		<main className="flex min-h-screen items-center justify-center bg-background px-4">
			<div className="w-full max-w-sm rounded-xl border border-border bg-card p-8 text-center">
				{children}
			</div>
		</main>
	)
}
